import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one,
   * then selecting the featured media for the shopper-selected variant.
   * Initial page render is intentionally left on the first product media item.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, resource } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const slides = Array.from(newMediaGallery.querySelectorAll('slideshow-slide'));
        const featuredMediaId = resource?.featured_media?.id;
        const featuredImageUrl = resource?.featured_image?.src;

        let featuredMediaIndex = -1;

        if (featuredMediaId != null) {
          featuredMediaIndex = slides.findIndex((slide) =>
            slide.querySelector(`.product-media[data-media-id="${featuredMediaId}"]`)
          );
        }

        if (featuredMediaIndex < 0 && featuredImageUrl) {
          const normalizedFeaturedImageUrl = this.#normalizeImageUrl(featuredImageUrl);
          featuredMediaIndex = slides.findIndex((slide) => {
            const image = slide.querySelector('.product-media__image');
            const imageUrl = image instanceof HTMLImageElement ? image.currentSrc || image.src : '';
            return imageUrl && this.#normalizeImageUrl(imageUrl) === normalizedFeaturedImageUrl;
          });
        }

        this.replaceWith(newMediaGallery);

        if (featuredMediaIndex >= 0) {
          requestAnimationFrame(() => {
            newMediaGallery.slideshow?.select(featuredMediaIndex, undefined, { animate: false });
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Normalizes Shopify image URLs so width/version query parameters do not prevent matching.
   * @param {string} value
   * @returns {string}
   */
  #normalizeImageUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return `${url.hostname}${url.pathname}`;
    } catch {
      return value;
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
